/**
 * Admin → Spots: browse / search the spot library without touching Mongo.
 *
 * Search by a spot's `_id` or its `source_conversation_id` (both are ids
 * you copy out of conversations), or page through newest-first. Each row
 * renders the spot + its captured **facts diagnostics** — the seeded hero
 * seat vs the emitted position, flagged on mismatch, so "our seed or LLM
 * drift?" is readable at a glance (see `app/facts/diagnostics.py`).
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  getLibrarySpots,
  type LibrarySpotItem,
} from "@/features/admin/lib/adminApi";
import { SimplifiedSpotCard } from "@/features/chat/components/SimplifiedSpotCard";
import type { SpotPayload } from "@/shared/models";
import { relativeTime } from "@/shared/format";

const PAGE_SIZE = 25;

export function SpotsView() {
  const [page, setPage] = useState(0);
  const [input, setInput] = useState("");
  const [spotId, setSpotId] = useState("");

  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: ["admin-library-spots", page, spotId],
    queryFn: () =>
      getLibrarySpots({ page, limit: PAGE_SIZE, spotId: spotId || undefined }),
  });

  function search() {
    setSpotId(input.trim());
    setPage(0);
  }
  function clear() {
    setInput("");
    setSpotId("");
    setPage(0);
  }

  return (
    <div className="space-y-5">
      <div className="flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && search()}
          placeholder="Search by spot id or conversation id…"
          className="flex-1 rounded-lg border border-border bg-bg/40 px-3 py-2 text-sm text-text placeholder-text-muted focus:outline-none focus:border-accent font-mono"
        />
        <button
          type="button"
          onClick={search}
          className="rounded-lg bg-accent/15 text-accent text-sm font-medium px-4 hover:bg-accent/25 transition"
        >
          Search
        </button>
        {spotId && (
          <button
            type="button"
            onClick={clear}
            className="rounded-lg border border-border text-text-muted text-sm font-medium px-4 hover:text-text hover:bg-text/5 transition"
          >
            Clear
          </button>
        )}
      </div>

      {isLoading && <p className="text-sm text-text-muted">Loading spots…</p>}
      {isError && (
        <p className="text-sm text-danger">Couldn't load library spots.</p>
      )}
      {data && data.items.length === 0 && (
        <p className="text-sm text-text-muted">
          {spotId ? "No spot found for that id." : "No spots in the library."}
        </p>
      )}

      {data && data.items.length > 0 && (
        <>
          <div className="space-y-5">
            {data.items.map((item) => (
              <SpotRow key={item.id} item={item} />
            ))}
          </div>

          {!spotId && (
            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                disabled={page === 0 || isFetching}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="rounded-lg border border-border text-text-muted px-4 py-2 disabled:opacity-40 hover:bg-text/5 hover:text-text transition"
              >
                ← Prev
              </button>
              <span className="text-text-muted/80 font-mono tabular-nums text-xs">
                page {page + 1} · {data.pageMeta.total} total
              </span>
              <button
                type="button"
                disabled={!data.pageMeta.hasNext || isFetching}
                onClick={() => setPage((p) => p + 1)}
                className="rounded-lg border border-border text-text-muted px-4 py-2 disabled:opacity-40 hover:bg-text/5 hover:text-text transition"
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SpotRow({ item }: { item: LibrarySpotItem }) {
  const badge = (
    <span className="text-[11px] rounded-full bg-accent/15 text-accent px-2 py-0.5 font-medium">
      {item.drillTarget ?? "uncategorized"}
      {item.drillDifficulty ? ` · ${item.drillDifficulty}` : ""}
    </span>
  );

  return (
    <SimplifiedSpotCard
      spot={item.spot as unknown as SpotPayload}
      topRight={badge}
      actions={<FactsPanel item={item} />}
    />
  );
}

function FactsPanel({ item }: { item: LibrarySpotItem }) {
  const hasFacts = item.facts != null;
  return (
    <div className="space-y-2 text-xs">
      {/* The load-bearing diagnostic: seed seat vs emitted position. */}
      {item.seedSeat ? (
        <div className="flex items-center gap-2">
          <span className="text-text-muted">Seat:</span>
          <span className="font-mono text-text">seed {item.seedSeat}</span>
          <span className="text-text-muted/60">→</span>
          <span className="font-mono text-text">
            emitted {item.emittedPosition ?? "?"}
          </span>
          {item.seatMismatch ? (
            <span className="rounded bg-danger/15 text-danger px-1.5 py-0.5 font-semibold">
              ⚠ MISMATCH
            </span>
          ) : (
            <span className="rounded bg-accent/15 text-accent px-1.5 py-0.5">
              ✓ match
            </span>
          )}
        </div>
      ) : (
        <div className="text-text-muted/70 italic">
          {hasFacts
            ? "Facts captured, but no seed (verdict-turn spot)."
            : "No facts captured (generated before diagnostics shipped)."}
        </div>
      )}

      <div className="text-text-muted/70 space-y-0.5">
        <p>
          {item.createdAt ? relativeTime(item.createdAt) : "unknown date"}
          {item.mode ? ` · ${item.mode}` : ""}
        </p>
        <p className="font-mono text-[10px] break-all">
          spot {item.id}
          {item.sourceConversationId ? ` · conv ${item.sourceConversationId}` : ""}
        </p>
      </div>

      {hasFacts && (
        <details>
          <summary className="cursor-pointer text-text-muted/70 hover:text-text">
            Raw facts JSON
          </summary>
          <pre className="mt-1 max-h-64 overflow-auto rounded-lg bg-bg/60 border border-border p-2 text-[10px] leading-relaxed text-text-muted">
            {JSON.stringify(item.facts, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}
